import { useState, useEffect, useCallback } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt    = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fmtTs  = (d) => d ? new Date(d).toLocaleString('en-GB',  { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
const money  = (n) => n != null ? `${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RWF` : '—'

const BATCH_STATUS = {
  draft:      { bg: '#fffbeb', color: '#b45309', label: 'Draft'      },
  exported:   { bg: '#eff6ff', color: '#1d4ed8', label: 'Exported'   },
  disbursed:  { bg: '#f0fdf4', color: '#15803d', label: 'Disbursed ✓'},
}

// ── PDF export ────────────────────────────────────────────────────────────────
function exportBatchPDF(batch, items, periodLabel) {
  const fmt2 = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })

  const rows = items.map((r, i) => `
    <tr style="background:${i % 2 === 0 ? '#fff' : '#f9f9f7'}">
      <td>${i + 1}</td>
      <td><strong>${r.shop_name}</strong><br><span style="font-size:10px;color:#666">${r.owner_name || ''}</span></td>
      <td>${r.bank_name || '—'}</td>
      <td style="font-family:monospace">${r.bank_account_no || '—'}</td>
      <td>${r.bank_holder || '—'}</td>
      <td style="text-align:right;font-weight:700">${fmt2(r.amount)}</td>
    </tr>`).join('')

  const total = items.reduce((s, r) => s + Number(r.amount || 0), 0)

  const html = `<!DOCTYPE html><html><head><title>Kudos Payout Batch — ${periodLabel}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,sans-serif;font-size:11px;padding:24px;color:#111}
    h1{font-size:18px;font-weight:900;margin-bottom:4px}
    .meta{font-size:11px;color:#666;margin-bottom:16px;font-family:monospace}
    .badge{display:inline-block;padding:3px 10px;border-radius:4px;font-size:10px;font-weight:700;background:#dbeafe;color:#1d4ed8;border:1px solid #93c5fd;margin-bottom:12px}
    table{width:100%;border-collapse:collapse;font-size:10px}
    th{background:#f3f4f6;text-align:left;padding:7px 8px;font-size:9px;text-transform:uppercase;letter-spacing:.07em;color:#6b7280;border-bottom:2px solid #e5e7eb}
    td{padding:7px 8px;border-bottom:1px solid #f3f4f6;vertical-align:top}
    .total td{font-weight:700;background:#f0fdf4;border-top:2px solid #e5e7eb;font-size:11px}
    .footer{margin-top:16px;font-size:10px;color:#9a9a94;font-family:monospace}
    @media print{body{padding:10px}@page{margin:15mm}}
  </style></head><body>
  <h1>Kudos Live — Seller Payout Batch</h1>
  <div class="meta">Period: ${periodLabel} · ${items.length} sellers · Batch #${batch.batch_id}</div>
  <div class="badge">FOR BANK USE — BULK TRANSFER LIST</div>
  <table>
    <thead><tr>
      <th>#</th><th>Shop / Seller</th><th>Bank</th><th>Account No.</th><th>Account Holder</th>
      <th style="text-align:right">Amount (RWF)</th>
    </tr></thead>
    <tbody>
      ${rows}
      <tr class="total">
        <td colspan="5">TOTAL — ${items.length} transfers</td>
        <td style="text-align:right">${fmt2(total)}</td>
      </tr>
    </tbody>
  </table>
  <div class="footer">
    Generated ${new Date().toLocaleString('en-GB')} · Kudos Live Admin · Batch ref: ${batch.bank_reference || 'pending'}
  </div>
  <script>window.onload=function(){window.print()}<\/script>
  </body></html>`

  const w = window.open('', '_blank')
  if (!w) return
  w.document.write(html)
  w.document.close()
}

// ── Sub-components ────────────────────────────────────────────────────────────
function SellerRow({ r, selected, onToggle, showOnHold }) {
  const hasBank    = !!r.bank_account_no
  const blocked    = r.payout_blocked
  return (
    <div
      onClick={() => !blocked && hasBank && onToggle(r.shop_id)}
      style={{ display: 'grid', gridTemplateColumns: '36px 2fr 1.2fr 1.4fr 1fr 1fr', padding: '12px 18px',
        borderBottom: '1px solid #f3f3f0', alignItems: 'center',
        background: selected ? '#fff3f0' : blocked ? '#fef9f9' : '#fff',
        cursor: blocked || !hasBank ? 'not-allowed' : 'pointer',
        opacity: blocked && !showOnHold ? 0.7 : 1,
        transition: 'background 0.1s' }}
      onMouseEnter={e => { if (!blocked && hasBank) e.currentTarget.style.background = selected ? '#ffe8df' : '#fafaf8' }}
      onMouseLeave={e => { e.currentTarget.style.background = selected ? '#fff3f0' : blocked ? '#fef9f9' : '#fff' }}>

      {/* Checkbox */}
      <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${selected ? '#e8440a' : blocked ? '#fecaca' : '#d1d5db'}`, background: selected ? '#e8440a' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {selected && <span style={{ color: '#fff', fontSize: 11, fontWeight: 900 }}>✓</span>}
      </div>

      {/* Shop */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.shop_name}</div>
        <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
          {r.owner_name} · {r.owner_phone}
        </div>
        <div style={{ display: 'flex', gap: 5, marginTop: 3, flexWrap: 'wrap' }}>
          {blocked && <span style={{ fontSize: 8, fontWeight: 700, background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', padding: '1px 5px', borderRadius: 3, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>⛔ {r.verification_status}</span>}
          {!hasBank && <span style={{ fontSize: 8, fontWeight: 700, background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', padding: '1px 5px', borderRadius: 3, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>⚠ No Bank</span>}
          {r.has_request && <span style={{ fontSize: 8, fontWeight: 700, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', padding: '1px 5px', borderRadius: 3, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>✓ Requested</span>}
          <span style={{ fontSize: 8, fontWeight: 700, background: r.payout_schedule === 'on_demand' ? '#f5f3ff' : '#f0fdf4', color: r.payout_schedule === 'on_demand' ? '#7c3aed' : '#15803d', border: `1px solid ${r.payout_schedule === 'on_demand' ? '#ddd6fe' : '#bbf7d0'}`, padding: '1px 5px', borderRadius: 3, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>
            {r.payout_schedule === 'on_demand' ? '⚡ On Demand' : '📅 Monthly'}
          </span>
        </div>
      </div>

      {/* Bank */}
      <div style={{ minWidth: 0 }}>
        {hasBank ? (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.bank_name || '—'}</div>
            <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.bank_account_no}</div>
          </>
        ) : (
          <div style={{ fontSize: 11, color: '#d1d5db', fontStyle: 'italic' }}>Not set</div>
        )}
      </div>

      {/* Account holder */}
      <div style={{ fontSize: 11, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.bank_holder || '—'}</div>

      {/* Orders */}
      <div style={{ fontSize: 12, fontWeight: 600, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{r.order_count}</div>

      {/* Amount */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{money(r.total_payout)}</div>
        <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
          P {Number(r.product_payout).toFixed(0)}
          {r.self_delivery_payout > 0 && ` + D ${Number(r.self_delivery_payout).toFixed(0)}`}
          {r.tips_net > 0 && ` + T ${Number(r.tips_net).toFixed(0)}`}
        </div>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PayoutBatch() {
  const now        = new Date()
  const weekStart  = new Date(now); weekStart.setDate(now.getDate() - now.getDay() + 1)
  const weekEnd    = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6)
  const toISO      = (d) => d.toISOString().slice(0, 10)

  const [tab,         setTab]         = useState('new')       // 'new' | 'history'
  const [dateFrom,    setDateFrom]    = useState(toISO(weekStart))
  const [dateTo,      setDateTo]      = useState(toISO(weekEnd))
  const [preview,     setPreview]     = useState(null)        // { payable, onhold, ... }
  const [loading,     setLoading]     = useState(false)
  const [selected,    setSelected]    = useState(new Set())   // shop_ids checked
  const [creating,    setCreating]    = useState(false)
  const [batchNotes,  setBatchNotes]  = useState('')
  const [batches,     setBatches]     = useState([])
  const [batchLoad,   setBatchLoad]   = useState(false)
  const [activeDetail,setActiveDetail]= useState(null)        // { batch, items }
  const [detailLoad,  setDetailLoad]  = useState(false)
  const [disbursing,  setDisbursing]  = useState(false)
  const [bankRef,     setBankRef]     = useState('')
  const [showDisburse,setShowDisburse]= useState(false)

  // Load history on mount
  useEffect(() => {
    setBatchLoad(true)
    api.get('/payments/admin/payout-batches')
      .then(res => setBatches(res.data.batches || []))
      .catch(() => setBatches([]))
      .finally(() => setBatchLoad(false))
  }, [])

  const handlePreview = useCallback(async () => {
    setLoading(true); setPreview(null); setSelected(new Set())
    try {
      const res = await api.get(`/payments/admin/payout-batch/preview`)
      setPreview(res.data)
      // Auto-select all payable sellers that have bank details
      setSelected(new Set(res.data.payable.filter(r => r.has_bank_details).map(r => r.shop_id)))
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load preview')
    } finally { setLoading(false) }
  }, [])

  const toggleAll = () => {
    const payableWithBank = (preview?.payable || []).filter(r => r.has_bank_details).map(r => r.shop_id)
    if (selected.size === payableWithBank.length) setSelected(new Set())
    else setSelected(new Set(payableWithBank))
  }

  const toggleOne = (shopId) => setSelected(prev => {
    const next = new Set(prev)
    next.has(shopId) ? next.delete(shopId) : next.add(shopId)
    return next
  })

  const selectedShops = (preview?.payable || []).filter(r => selected.has(r.shop_id))
  const selectedTotal = selectedShops.reduce((s, r) => s + Number(r.total_payout || 0), 0)

  const handleCreateBatch = async () => {
    if (!selectedShops.length) { alert('Select at least one seller.'); return }
    setCreating(true)
    try {
      const res = await api.post('/payments/admin/payout-batch/create', {
        period_from: dateFrom,
        period_to:   dateTo,
        notes:       batchNotes.trim() || null,
        shops:       selectedShops.map(r => ({ shop_id: r.shop_id, total_payout: r.total_payout, request_id: r.request_id || null })),
      })
      setBatches(prev => [res.data.batch, ...prev])
      setTab('history')
      loadBatchDetail(res.data.batch.batch_id)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create batch')
    } finally { setCreating(false) }
  }

  const loadBatchDetail = async (batchId) => {
    setDetailLoad(true); setActiveDetail(null); setBankRef(''); setShowDisburse(false)
    try {
      const res = await api.get(`/payments/admin/payout-batch/${batchId}`)
      setActiveDetail(res.data)
    } catch { setActiveDetail(null) }
    finally { setDetailLoad(false) }
  }

  const handleMarkExported = async () => {
    if (!activeDetail) return
    try {
      const res = await api.patch(`/payments/admin/payout-batch/${activeDetail.batch.batch_id}/mark-exported`)
      setActiveDetail(d => ({ ...d, batch: res.data.batch }))
      setBatches(prev => prev.map(b => b.batch_id === res.data.batch.batch_id ? res.data.batch : b))
    } catch { alert('Failed to mark as exported') }
  }

  const handleDisburse = async () => {
    if (!activeDetail) return
    setDisbursing(true)
    try {
      const res = await api.patch(`/payments/admin/payout-batch/${activeDetail.batch.batch_id}/disburse`, { bank_reference: bankRef.trim() || null })
      setActiveDetail(d => ({ ...d, batch: res.data.batch }))
      setBatches(prev => prev.map(b => b.batch_id === res.data.batch.batch_id ? res.data.batch : b))
      setShowDisburse(false)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to disburse')
    } finally { setDisbursing(false) }
  }

  const periodLabel = `${fmt(activeDetail?.batch.period_from || dateFrom)} – ${fmt(activeDetail?.batch.period_to || dateTo)}`

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Payout Batches</div>
        <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>
          Generate bank transfer lists for seller payouts — processed twice a week.
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 20, width: 'fit-content' }}>
        {[['new', '+ New Batch'], ['history', `History (${batches.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding: '10px 22px', border: 'none', background: tab === id ? '#111317' : 'transparent', color: tab === id ? '#fff' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'all 0.12s' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ══ NEW BATCH TAB ══ */}
      {tab === 'new' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Preview trigger */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '14px 18px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111317' }}>All unsettled earnings</div>
              <div style={{ fontSize: 12, color: '#9a9a94', marginTop: 2 }}>
                Shows every delivered order that hasn't been included in a previous batch — no date range needed.
                Mid-week requests and end-of-week payouts are automatically kept separate.
              </div>
            </div>
            <button onClick={handlePreview} disabled={loading}
              style={{ height: 42, padding: '0 20px', borderRadius: 9, border: 'none', background: loading ? '#bfdbfe' : '#1d4ed8', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              {loading ? '⏳ Loading…' : '⟳ Load Payout Preview'}
            </button>
          </div>

          {preview && (
            <>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
                {[
                  { label: 'Payable Sellers',   value: preview.payable.length,                                               accent: '#15803d' },
                  { label: 'Payable Total',     value: money(preview.payable_total),                                         accent: '#15803d', bold: true },
                  { label: 'Requested',         value: `${preview.requests_count || 0} + ${preview.requested_only?.length || 0}`, accent: '#7c3aed', sub: 'active + pending-only' },
                  { label: 'On Hold (blocked)', value: preview.onhold.length,                                                accent: '#b91c1c' },
                ].map(c => (
                  <div key={c.label} style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '16px 18px' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{c.label}</div>
                    <div style={{ fontSize: c.bold ? 20 : 22, fontWeight: 800, color: c.accent }}>{c.value}</div>
                    {c.sub && <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 3 }}>{c.sub}</div>}
                  </div>
                ))}
              </div>

              {/* Selected total banner */}
              {selected.size > 0 && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 2 }}>
                      Selected for this batch
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>{money(selectedTotal)}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>{selected.size} seller{selected.size !== 1 ? 's' : ''}</div>
                  </div>
                  <div style={{ flex: 1 }} />
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input value={batchNotes} onChange={e => setBatchNotes(e.target.value)}
                      placeholder="Optional notes (e.g. 'Week 23 batch')"
                      style={{ height: 38, borderRadius: 8, border: '1px solid #bbf7d0', padding: '0 12px', fontSize: 12, outline: 'none', width: 240, fontFamily: "'Plus Jakarta Sans',sans-serif", background: 'rgba(255,255,255,0.7)' }} />
                    <button onClick={handleCreateBatch} disabled={creating}
                      style={{ height: 42, padding: '0 20px', borderRadius: 9, border: 'none', background: creating ? '#bbf7d0' : '#15803d', color: '#fff', fontSize: 13, fontWeight: 700, cursor: creating ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", whiteSpace: 'nowrap' }}>
                      {creating ? 'Creating…' : '✓ Create Batch →'}
                    </button>
                  </div>
                </div>
              )}

              {/* ── Payable sellers table ── */}
              {preview.payable.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '36px 2fr 1.2fr 1.4fr 1fr 1fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7', alignItems: 'center' }}>
                    <div onClick={toggleAll} style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${selected.size > 0 ? '#e8440a' : '#d1d5db'}`, background: selected.size > 0 ? '#e8440a' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                      {selected.size > 0 && <span style={{ color: '#fff', fontSize: 11, fontWeight: 900 }}>✓</span>}
                    </div>
                    {['Shop / Seller', 'Bank', 'Account Holder', 'Orders', 'Net Payout'].map(h => (
                      <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
                    ))}
                  </div>
                  {preview.payable.map(r => (
                    <SellerRow key={r.shop_id} r={r} selected={selected.has(r.shop_id)} onToggle={toggleOne} />
                  ))}
                  {/* Footer total */}
                  <div style={{ display: 'grid', gridTemplateColumns: '36px 2fr 1.2fr 1.4fr 1fr 1fr', padding: '12px 18px', borderTop: '2px solid #e9e9e4', background: '#f9f9f7', alignItems: 'center' }}>
                    <div />
                    <div style={{ fontSize: 12, fontWeight: 800 }}>All payable ({preview.payable.length})</div>
                    <div /><div /><div />
                    <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{money(preview.payable_total)}</div>
                  </div>
                </div>
              )}

              {preview.payable.length === 0 && (
                <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>
                  No payable sellers with unsettled earnings.
                </div>
              )}

              {/* ── Requested-only: have pending requests but no remaining unsettled earnings ── */}
              {preview.requested_only?.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #ddd6fe', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '12px 18px', borderBottom: '1px solid #ddd6fe', background: '#faf5ff', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 16 }}>📋</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#7c3aed' }}>Pending Requests — No Unsettled Earnings ({preview.requested_only.length})</div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                        These sellers submitted a payout request but their orders may already be settled. Review before including.
                      </div>
                    </div>
                  </div>
                  {preview.requested_only.map((r, i) => (
                    <div key={r.shop_id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', padding: '12px 18px', borderBottom: i < preview.requested_only.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{r.shop_name || `Shop #${r.shop_id}`}</div>
                        <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                          Request #{r.request_id} · Requested amount: {money(r.total_payout)}
                        </div>
                      </div>
                      <div>
                        <span style={{ fontSize: 9, fontWeight: 700, background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', padding: '2px 7px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>
                          ✓ Requested
                        </span>
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>{money(r.total_payout)}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── On Hold table ── */}
              {preview.onhold.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #fecaca', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '12px 18px', borderBottom: '1px solid #fecaca', background: '#fef2f2', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 16 }}>⛔</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#b91c1c' }}>On Hold — Verification Blocked ({preview.onhold.length} sellers)</div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                        Total owed: <strong>{money(preview.onhold_total)}</strong> — cannot disburse until sellers are verified.
                        Money accumulates here; once verified they will appear on the next batch.
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '36px 2fr 1.2fr 1.4fr 1fr 1fr', padding: '10px 18px', borderBottom: '1px solid #fecaca', background: '#fff5f5' }}>
                    <div />
                    {['Shop / Seller', 'Bank', 'Account Holder', 'Orders', 'Owed'].map(h => (
                      <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
                    ))}
                  </div>
                  {preview.onhold.map(r => (
                    <SellerRow key={r.shop_id} r={r} selected={false} onToggle={() => {}} showOnHold />
                  ))}
                  <div style={{ display: 'grid', gridTemplateColumns: '36px 2fr 1.2fr 1.4fr 1fr 1fr', padding: '12px 18px', borderTop: '2px solid #fecaca', background: '#fff5f5', alignItems: 'center' }}>
                    <div />
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#b91c1c' }}>On hold ({preview.onhold.length})</div>
                    <div /><div /><div />
                    <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>{money(preview.onhold_total)}</div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ══ HISTORY TAB ══ */}
      {tab === 'history' && (
        <div style={{ display: 'flex', gap: 20 }}>

          {/* Batch list */}
          <div style={{ width: 320, flexShrink: 0 }}>
            {batchLoad ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading…</div>
            ) : batches.length === 0 ? (
              <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '40px', textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>💳</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 4 }}>No batches yet</div>
                <div style={{ fontSize: 12, color: '#9a9a94' }}>Create your first batch from the New Batch tab.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {batches.map(b => {
                  const st = BATCH_STATUS[b.status] || BATCH_STATUS.draft
                  const isActive = activeDetail?.batch.batch_id === b.batch_id
                  return (
                    <div key={b.batch_id} onClick={() => loadBatchDetail(b.batch_id)}
                      style={{ background: isActive ? '#fff3f0' : '#fff', border: `1.5px solid ${isActive ? '#e8440a' : '#e9e9e4'}`, borderRadius: 10, padding: '14px 16px', cursor: 'pointer', transition: 'all 0.12s' }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#fafaf8' }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = '#fff' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div style={{ fontSize: 12, fontWeight: 800 }}>Batch #{b.batch_id}</div>
                        <span style={{ background: st.bg, color: st.color, fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>{st.label}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#374151', fontFamily: "'JetBrains Mono',monospace", marginBottom: 3 }}>{fmt(b.period_from)} – {fmt(b.period_to)}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>{money(b.total_amount)}</div>
                      <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>{b.shop_count} sellers · {fmtTs(b.created_at)}</div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Batch detail */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {detailLoad && (
              <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading batch…</div>
            )}

            {!detailLoad && !activeDetail && (
              <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '60px', textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>👈</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>Select a batch to view details</div>
              </div>
            )}

            {!detailLoad && activeDetail && (() => {
              const { batch, items } = activeDetail
              const st = BATCH_STATUS[batch.status] || BATCH_STATUS.draft
              const isDisbursed = batch.status === 'disbursed'

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {/* Batch header */}
                  <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '18px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                          <div style={{ fontSize: 18, fontWeight: 900 }}>Batch #{batch.batch_id}</div>
                          <span style={{ background: st.bg, color: st.color, fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>{st.label}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                          Period: {fmt(batch.period_from)} – {fmt(batch.period_to)} · {batch.shop_count} sellers · Created {fmtTs(batch.created_at)}
                        </div>
                        {batch.bank_reference && (
                          <div style={{ fontSize: 11, color: '#15803d', fontFamily: "'JetBrains Mono',monospace", marginTop: 4 }}>
                            Bank ref: <strong>{batch.bank_reference}</strong> · Disbursed {fmtTs(batch.disbursed_at)}
                          </div>
                        )}
                        {batch.notes && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>{batch.notes}</div>}
                      </div>
                      <div style={{ fontSize: 26, fontWeight: 900, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>{money(batch.total_amount)}</div>
                    </div>

                    {/* Action buttons */}
                    {!isDisbursed && (
                      <div style={{ display: 'flex', gap: 10, marginTop: 14, paddingTop: 14, borderTop: '1px solid #e9e9e4', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            exportBatchPDF(batch, items, `${fmt(batch.period_from)} – ${fmt(batch.period_to)}`)
                            handleMarkExported()
                          }}
                          style={{ height: 38, padding: '0 16px', borderRadius: 8, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6 }}>
                          ⬇ Export PDF for Bank
                        </button>
                        <button onClick={() => setShowDisburse(true)}
                          style={{ height: 38, padding: '0 16px', borderRadius: 8, border: 'none', background: '#15803d', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                          ✓ Mark as Disbursed
                        </button>
                      </div>
                    )}

                    {isDisbursed && (
                      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 20 }}>✅</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>Disbursed — transfers sent to bank</div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>All sellers in this batch have been marked as paid.</div>
                        </div>
                        <button onClick={() => exportBatchPDF(batch, items, `${fmt(batch.period_from)} – ${fmt(batch.period_to)}`)}
                          style={{ marginLeft: 'auto', height: 34, padding: '0 14px', borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                          ⬇ Re-export PDF
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Disburse modal */}
                  {showDisburse && (
                    <>
                      <div onClick={() => setShowDisburse(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 500 }} />
                      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 400, background: '#fff', borderRadius: 14, zIndex: 501, boxShadow: '0 24px 80px rgba(0,0,0,0.18)', overflow: 'hidden', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontSize: 16, fontWeight: 800 }}>✓ Confirm Disbursement</div>
                          <div onClick={() => setShowDisburse(false)} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94' }}>×</div>
                        </div>
                        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 9, padding: '12px 14px' }}>
                            <div style={{ fontSize: 11, color: '#15803d', fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>Batch #{batch.batch_id} · {batch.shop_count} sellers</div>
                            <div style={{ fontSize: 22, fontWeight: 900, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>{money(batch.total_amount)}</div>
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 5 }}>
                              Bank Reference (optional)
                            </label>
                            <input value={bankRef} onChange={e => setBankRef(e.target.value)}
                              placeholder="Bank transaction or batch reference number"
                              style={{ height: 42, borderRadius: 8, border: '1px solid #e9e9e4', padding: '0 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7280', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px' }}>
                            ⚠ This will mark all {batch.shop_count} sellers in this batch as <strong>Paid</strong>. This cannot be undone.
                          </div>
                          <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setShowDisburse(false)}
                              style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                              Cancel
                            </button>
                            <button onClick={handleDisburse} disabled={disbursing}
                              style={{ flex: 2, height: 42, borderRadius: 8, border: 'none', background: disbursing ? '#bbf7d0' : '#15803d', color: '#fff', fontSize: 13, fontWeight: 700, cursor: disbursing ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                              {disbursing ? 'Processing…' : '✓ Confirm — Mark as Paid'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Items table */}
                  <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '0.4fr 2fr 1.2fr 1.4fr 1fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
                      {['#', 'Shop / Seller', 'Bank', 'Account Holder', 'Amount'].map(h => (
                        <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
                      ))}
                    </div>
                    {items.map((r, i) => (
                      <div key={r.request_id} style={{ display: 'grid', gridTemplateColumns: '0.4fr 2fr 1.2fr 1.4fr 1fr', padding: '12px 18px', borderBottom: i < items.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>
                        <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{i + 1}</div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>{r.shop_name}</div>
                          <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{r.owner_name} · {r.owner_phone}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600 }}>{r.bank_name || '—'}</div>
                          <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{r.bank_account_no || '—'}</div>
                        </div>
                        <div style={{ fontSize: 11, color: '#374151' }}>{r.bank_holder || '—'}</div>
                        <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{money(r.amount)}</div>
                      </div>
                    ))}
                    <div style={{ display: 'grid', gridTemplateColumns: '0.4fr 2fr 1.2fr 1.4fr 1fr', padding: '12px 18px', borderTop: '2px solid #e9e9e4', background: '#f0fdf4', alignItems: 'center' }}>
                      <div />
                      <div style={{ fontSize: 12, fontWeight: 800 }}>Total ({items.length} sellers)</div>
                      <div /><div />
                      <div style={{ fontSize: 14, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{money(batch.total_amount)}</div>
                    </div>
                  </div>
                </div>
              )
            })()}
          </div>
        </div>
      )}
    </div>
  )
}
